import * as THREE from 'three'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js'
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js'
import { createEdgeOutline } from '../render/edgeOutline'

export type PostQuality = 'low' | 'medium' | 'high'

/**
 * `diorama` is the region: a handmade model on a table, so tilt-shift sells the
 * miniature. `ground` is standing in a street at eye level, where the same blur
 * would read as a broken lens.
 */
export type PostStyle = 'diorama' | 'ground'

export interface PostFx {
  render: (dt: number) => void
  setSize: (width: number, height: number) => void
  dispose: () => void
  quality: PostQuality
  /** World-space sun position for crepuscular rays. */
  setSun: (world: THREE.Vector3) => void
}

export interface PostFxOptions {
  quality?: PostQuality
  style?: PostStyle
  /** Depth-edge ink lines. The other half of the cel look, with `toon.ts`. */
  outline?: boolean
}

/**
 * Screen-space tilt-shift. Blur is driven by distance from a horizontal focus
 * band rather than depth, which is the classic miniature-faking technique and
 * keeps the world reading as a handmade diorama.
 */
const TiltShiftShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    resolution: { value: new THREE.Vector2(1, 1) },
    direction: { value: new THREE.Vector2(1, 0) },
    focusCenter: { value: 0.54 },
    focusWidth: { value: 0.26 },
    strength: { value: 1.6 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 resolution;
    uniform vec2 direction;
    uniform float focusCenter;
    uniform float focusWidth;
    uniform float strength;
    varying vec2 vUv;

    void main() {
      float distanceFromFocus = abs(vUv.y - focusCenter);
      float blur = smoothstep(focusWidth, focusWidth + 0.42, distanceFromFocus);
      float radius = blur * strength;

      if (radius < 0.01) {
        gl_FragColor = texture2D(tDiffuse, vUv);
        return;
      }

      vec2 step = direction * radius / resolution;
      vec4 sum = texture2D(tDiffuse, vUv) * 0.2270270270;
      sum += texture2D(tDiffuse, vUv + step * 1.3846153846) * 0.3162162162;
      sum += texture2D(tDiffuse, vUv - step * 1.3846153846) * 0.3162162162;
      sum += texture2D(tDiffuse, vUv + step * 3.2307692308) * 0.0702702703;
      sum += texture2D(tDiffuse, vUv - step * 3.2307692308) * 0.0702702703;
      gl_FragColor = sum;
    }
  `,
}

/** Filmic grade: split-toning, gentle contrast, saturation lift, vignette. */
const CinematicGradeShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    saturation: { value: 1.14 },
    contrast: { value: 1.07 },
    vignette: { value: 0.34 },
    shadowTint: { value: new THREE.Color(0x1b3a4a) },
    highlightTint: { value: new THREE.Color(0xffe7bd) },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float saturation;
    uniform float contrast;
    uniform float vignette;
    uniform vec3 shadowTint;
    uniform vec3 highlightTint;
    varying vec2 vUv;

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      vec3 color = texel.rgb;

      float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      color = mix(vec3(luma), color, saturation);
      color = clamp((color - 0.5) * contrast + 0.5, 0.0, 1.0);

      // Push shadows cool and highlights warm so green mass separates by depth.
      vec3 shadowShift = mix(shadowTint, vec3(0.5), 0.5) * 2.0 - 1.0;
      vec3 highlightShift = mix(highlightTint, vec3(0.5), 0.5) * 2.0 - 1.0;
      color += shadowShift * (1.0 - luma) * 0.055;
      color += highlightShift * luma * 0.05;

      vec2 centered = vUv - 0.5;
      float falloff = 1.0 - dot(centered, centered) * vignette * 2.0;
      color *= clamp(falloff, 0.0, 1.0);

      gl_FragColor = vec4(clamp(color, 0.0, 1.0), texel.a);
    }
  `,
}

/** Crepuscular rays from the sun, blocked by the cone silhouette. */
const GodRaysShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    sunPos: { value: new THREE.Vector2(0.5, 0.7) },
    strength: { value: 0.42 },
    decay: { value: 0.94 },
    density: { value: 0.92 },
    exposure: { value: 0.28 },
    enabled: { value: 1 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 sunPos;
    uniform float strength;
    uniform float decay;
    uniform float density;
    uniform float exposure;
    uniform float enabled;
    varying vec2 vUv;

    void main() {
      vec4 base = texture2D(tDiffuse, vUv);
      if (enabled < 0.5) {
        gl_FragColor = base;
        return;
      }
      vec2 delta = (sunPos - vUv) * density / 24.0;
      vec2 coord = vUv;
      float illum = 1.0;
      vec3 rays = vec3(0.0);
      for (int i = 0; i < 24; i++) {
        coord += delta;
        if (coord.x < 0.0 || coord.x > 1.0 || coord.y < 0.0 || coord.y > 1.0) break;
        vec3 sampleCol = texture2D(tDiffuse, coord).rgb;
        float bright = max(0.0, dot(sampleCol, vec3(0.33)) - 0.78);
        rays += sampleCol * bright * illum;
        illum *= decay;
      }
      vec3 color = base.rgb + rays * strength * exposure;
      gl_FragColor = vec4(color, base.a);
    }
  `,
}

function detectQuality(): PostQuality {
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches
  const cores = navigator.hardwareConcurrency ?? 4
  const smallViewport = Math.min(window.innerWidth, window.innerHeight) < 620
  if (coarsePointer && (cores <= 4 || smallViewport)) return 'low'
  if (coarsePointer || cores <= 4) return 'medium'
  return 'high'
}

export function createPostFx(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
  options: PostFxOptions = {},
): PostFx {
  const quality = options.quality ?? detectQuality()
  const style = options.style ?? 'diorama'
  const ground = style === 'ground'
  const wantOutline = options.outline ?? false
  const size = renderer.getSize(new THREE.Vector2())

  // Ink is worth a composer even on a phone; everything else is not.
  if (quality === 'low' && !wantOutline) {
    return {
      quality,
      render: () => renderer.render(scene, camera),
      setSize: () => {},
      dispose: () => {},
      setSun: () => {},
    }
  }

  const edge = wantOutline
    ? createEdgeOutline(
        camera,
        size.x,
        size.y,
        ground
          ? // Softer ink — handmade lines, not hard anime cel.
            { thickness: 0.95, strength: 0.58, threshold: 0.026 }
          : // The region is seen from far enough that the same settings would
            // scribble over every ridge on the DEM.
            { thickness: 1.0, strength: 0.62, threshold: 0.03 },
      )
    : null

  let composer: EffectComposer
  if (edge) {
    // Deliberately no depthTexture on the base target: the composer clones it
    // for the second buffer, and a depth texture may only belong to one render
    // target at a time. `RenderPass` writes into readBuffer, which is
    // renderTarget2, so that is the only buffer that gets the attachment. The
    // edge pass then writes into renderTarget1 — if both carried the depth
    // texture, the pass would sample an attachment of the framebuffer it is
    // drawing into and WebGL would hand back a black frame.
    composer = new EffectComposer(
      renderer,
      new THREE.WebGLRenderTarget(size.x, size.y, {
        type: THREE.HalfFloatType,
        depthBuffer: true,
      }),
    )
    composer.renderTarget2.depthTexture = edge.depthTexture
  } else {
    composer = new EffectComposer(renderer)
  }

  composer.setPixelRatio(renderer.getPixelRatio())
  composer.setSize(size.x, size.y)
  composer.addPass(new RenderPass(scene, camera))

  // Ink goes down before bloom and grade, so highlights bleed over the lines
  // and the grade unifies both instead of sitting on raw black.
  if (edge) composer.addPass(edge.pass)

  let gtao: GTAOPass | null = null
  if (quality === 'high') {
    gtao = new GTAOPass(scene, camera, size.x, size.y)
    gtao.output = GTAOPass.OUTPUT.Default
    gtao.blendIntensity = ground ? 0.48 : 0.62
    gtao.updateGtaoMaterial({
      // Region units are 90 m; zone units are 1 m. The same number would either
      // do nothing at street level or occlude whole valleys from the air.
      radius: ground ? 0.9 : 3.4,
      distanceExponent: 1.1,
      thickness: ground ? 0.6 : 1.6,
      scale: 1.0,
      samples: 12,
      screenSpaceRadius: false,
    })
    composer.addPass(gtao)
  }

  const bloom = new UnrealBloomPass(
    new THREE.Vector2(size.x, size.y),
    ground ? 0.18 : quality === 'high' ? 0.38 : 0.3,
    ground ? 0.6 : 0.72,
    ground ? 0.86 : 0.82,
  )
  composer.addPass(bloom)

  // Screen-space radial blur of the colour buffer smears roofs and grass into
  // streaks. Keep it for the region diorama (sun is a small disc); on foot the
  // cone shafts are world-space meshes instead.
  const godRays = ground ? null : new ShaderPass(GodRaysShader)
  if (godRays) {
    godRays.uniforms.strength.value = 0.38
    godRays.uniforms.exposure.value = 0.22
    composer.addPass(godRays)
  }

  composer.addPass(new OutputPass())

  let horizontalBlur: ShaderPass | null = null
  let verticalBlur: ShaderPass | null = null
  if (!ground) {
    horizontalBlur = new ShaderPass(TiltShiftShader)
    verticalBlur = new ShaderPass(TiltShiftShader)
    verticalBlur.uniforms.direction.value = new THREE.Vector2(0, 1)
    composer.addPass(horizontalBlur)
    composer.addPass(verticalBlur)
  }

  const grade = new ShaderPass(CinematicGradeShader)
  if (ground) {
    // Soft hand-drawn miniature: cooler paper shadows, warm highlights, gentle
    // vignette — Messenger feeling via grade, not their assets.
    grade.uniforms.saturation.value = 0.88
    grade.uniforms.contrast.value = 1.03
    grade.uniforms.vignette.value = 0.32
    grade.uniforms.shadowTint.value.set(0x3a5a62)
    grade.uniforms.highlightTint.value.set(0xfff0d4)
  }
  composer.addPass(grade)

  if (quality === 'high') composer.addPass(new SMAAPass(size.x, size.y))

  const applyResolution = (width: number, height: number) => {
    horizontalBlur?.uniforms.resolution.value.set(width, height)
    verticalBlur?.uniforms.resolution.value.set(width, height)
    // Sample in render-target pixels, which pixel ratio has already scaled.
    if (edge) edge.setSize(composer.renderTarget2.width, composer.renderTarget2.height)
  }
  applyResolution(size.x, size.y)

  const sunNdc = new THREE.Vector3()
  const setSun = (world: THREE.Vector3) => {
    if (!godRays) return
    sunNdc.copy(world).project(camera)
    const u = sunNdc.x * 0.5 + 0.5
    const v = sunNdc.y * 0.5 + 0.5
    const onScreen = sunNdc.z < 1 && u > -0.05 && u < 1.05 && v > -0.05 && v < 1.05
    godRays.uniforms.enabled.value = onScreen ? 1 : 0
    godRays.uniforms.sunPos.value.set(u, v)
  }

  return {
    quality,
    setSun,
    render: (dt) => {
      // The composer leaves the buffers wherever the last frame's swap count
      // put them, so which target holds the depth attachment would alternate.
      // Pin them: the scene always lands in the buffer that owns the depth.
      if (edge) {
        composer.readBuffer = composer.renderTarget2
        composer.writeBuffer = composer.renderTarget1
      }
      composer.render(dt)
    },
    setSize: (width, height) => {
      composer.setSize(width, height)
      gtao?.setSize(width, height)
      applyResolution(width, height)
    },
    dispose: () => {
      edge?.dispose()
      composer.dispose()
    },
  }
}
